import { useLingui } from "@lingui/react/macro";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  PhotoCropper,
} from "@rakazo/ui-web";
import { X } from "lucide-react";
import { useRef, useState } from "react";
import { errorText } from "../lib/user-error";

type Props = {
  source: string | null;
  fit?: "cover" | "contain";
  shape?: "circle" | "square";
  onApply: (image: string) => Promise<void>;
  onCancel: () => void;
};

export function PhotoCropDialog(props: Props) {
  if (!props.source) return null;
  return <CropDialogSession key={props.source} {...props} source={props.source} />;
}

function CropDialogSession({ source, fit, shape, onApply, onCancel }: Props & { source: string }) {
  const { t } = useLingui();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const applying = useRef(false);
  async function apply(image: string) {
    if (applying.current) return;
    applying.current = true;
    setBusy(true);
    setError(null);
    try {
      await onApply(image);
      onCancel();
    } catch (cause) {
      setError(errorText(cause, t`Could not save this photo. Try a smaller image.`));
    } finally {
      applying.current = false;
      setBusy(false);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !busy) onCancel();
      }}
    >
      <DialogContent
        showCloseButton={false}
        data-testid="photo-crop-dialog"
        className="max-h-[calc(100dvh-24px)] w-[520px] max-w-[calc(100vw-24px)] gap-4 overflow-y-auto rounded-3xl p-5 sm:max-w-[520px]"
      >
        <DialogHeader className="relative items-center space-y-2 text-center">
          <DialogTitle>{t`Adjust Photo`}</DialogTitle>
          <DialogDescription className="text-base">{t`Drag to move, use the slider to zoom.`}</DialogDescription>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            disabled={busy}
            onClick={onCancel}
            aria-label={t`Close`}
            className="absolute end-0 top-0 rounded-full text-muted-foreground"
          >
            <X className="size-4" />
          </Button>
        </DialogHeader>
        <PhotoCropper
          src={source}
          fit={fit}
          shape={shape}
          disabled={busy}
          onApply={apply}
          onCancel={onCancel}
          labels={{
            preview: t`Photo crop. Drag or use arrow keys to reposition.`,
            zoom: t`Zoom`,
            zoomIn: t`Zoom in`,
            zoomOut: t`Zoom out`,
            cancel: t`Cancel`,
            reset: t`Reset`,
            apply: t`Apply Crop`,
            error: t`Could not crop this image. Choose another photo.`,
            loading: t`Loading photo…`,
          }}
        />
        {error ? (
          <p role="alert" className="text-base text-destructive">
            {error}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
